import { LDateTime } from '@app/components/date/LDateTime';
import { AuditReportsBroadcastEvidenceConfigDto } from '@mi-front/api-v2';
import { toDateTimeStr } from '@mi-front/core';
import { MrtTable } from '@mi-front/ui';
import { Chip } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';

type EvidenceEntry = AuditReportsBroadcastEvidenceConfigDto['evidence'][number];

interface Props {
  evidence: EvidenceEntry[];
}

/**
 * Simple table of evidence rows for a broadcast evidence report.
 */
export function BroadcastEvidenceTable({ evidence }: Props) {
  const { $t } = useIntl();

  return (
    <MrtTable<EvidenceEntry>
      data={evidence}
      columns={[
        {
          id: 'broadcastDatetime',
          header: $t({
            id: 'audit.broadcastEvidence.table.broadcastDatetime',
            defaultMessage: 'Emisión',
          }),
          accessorFn: (row) => row.broadcastDatetime,
          Cell: ({ row: { original } }) => (
            <LDateTime value={toDateTimeStr(original.broadcastDatetime)} />
          ),
          size: 140,
        },
        {
          id: 'scheduledMaterialId',
          header: $t({
            id: 'audit.broadcastEvidence.table.scheduledMaterialId',
            defaultMessage: 'Material pautado',
          }),
          accessorFn: (row) => row.scheduledMaterialId,
          size: 120,
        },
        {
          id: 'broadcastMaterialId',
          header: $t({
            id: 'audit.broadcastEvidence.table.broadcastMaterialId',
            defaultMessage: 'Material emitido',
          }),
          accessorFn: (row) => row.broadcastMaterialId,
          size: 120,
        },
        {
          id: 'methods',
          header: $t({
            id: 'audit.broadcastEvidence.table.methods',
            defaultMessage: 'Métodos',
          }),
          accessorFn: (row) => row.methods.join(', '),
          Cell: ({ row: { original } }) => (
            <>
              {original.methods.map((method) => (
                <Chip key={method} label={method} size="small" sx={{ mr: 0.5 }} />
              ))}
            </>
          ),
          size: 100,
        },
        {
          id: 'status',
          header: $t({
            id: 'audit.broadcastEvidence.table.status',
            defaultMessage: 'Estado',
          }),
          accessorFn: (row) => row.status,
          size: 100,
        },
        {
          id: 'withinTolerance',
          header: $t({
            id: 'audit.broadcastEvidence.table.withinTolerance',
            defaultMessage: 'Tolerancia',
          }),
          accessorFn: (row) => row.withinTolerance,
          Cell: ({ row: { original } }) =>
            original.withinTolerance ? (
              <FormattedMessage
                id="audit.broadcastEvidence.withinTolerance.yes"
                defaultMessage="Sí"
              />
            ) : (
              <FormattedMessage
                id="audit.broadcastEvidence.withinTolerance.no"
                defaultMessage="No"
              />
            ),
          size: 90,
        },
        {
          id: 'observation',
          header: $t({
            id: 'audit.broadcastEvidence.table.observation',
            defaultMessage: 'Observación',
          }),
          accessorFn: (row) => row.observation ?? '',
          size: 160,
        },
      ]}
      enablePagination={evidence.length > 25}
      enableGlobalFilter
      enableColumnFilters={false}
      enableHiding={false}
      enableColumnActions={false}
      enableSorting
    />
  );
}
